import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { get } from 'lodash';

import { HIGHLIGHT_TYPES } from '../../../constants/exercise';
import MathContent from '../../math-content';

import CheckBoxErrorIcon from './assets/multiplySquare.svg?react';
import CheckBoxCorrectIcon from './assets/checkmarkSquare.svg?react';
import CheckBoxSelectedIcon from './assets/checkboxChecked.svg?react';
import CheckBoxUnselectedIcon from './assets/checkbox.svg?react';

const Container = styled.label`
    & > svg {
        margin-left: 12px;
        margin-right: 8px;
        margin-top: 12px;
        width: 18px;
        min-width: 18px;
        height: 18px;
        fill: ${(props) => {
            return get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.checkmarkColor`);
        }};
    }
    display: flex;
    width: 100%;
    border-radius: 4px;
    min-height: 44px;
    box-sizing: border-box;
    border: 1px solid ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.borderColor`)};
    font-size: 16px;
    color: ${(props) => get(textColors, props.disabled && props.highlight === 'normal')};
    background-color: ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.backgroundColor`)};
    line-height: 24px;

    cursor: ${(props) => (props.disabled ? 'initial' : 'pointer')};
    overflow-x: auto;

    &:hover {
        border: 1px solid ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.hoverBorderColor`)};
        background-color: ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.hoverBackgroundColor`)};
    }

    &:focus-within {
        border: 1px solid ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.hoverBorderColor`)};
        background-color: ${(props) => get(statusColors, `${props.disabled}.${props.highlight}.${props.selected}.hoverBackgroundColor`)};
        box-shadow: var(--outline);
    }

    @media (max-width: 767px) {
        margin-right: 16px;
    }
`;

const Check = styled.input`
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;

    &:checked ~ div:after {
        display: block;
    }
`;

//.mjx-chtml serve per evitare scroll su particolari matematiche LAZE-1136 dentro scelta multipla
const Content = styled.div`
    margin: 8px 16px 8px 0px;
    overflow-x: auto;
    width: 100%;

    .mjx-chtml {
        overflow: hidden;
    }

    p:first-of-type {
        margin: 0;
    }
`;

const textColors = {
    false: 'var(--text--gray800)',
    true: 'var(--gray700)',
};

const statusColors = {
    true: {
        // disabled
        normal: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--gray200)',
                backgroundColor: 'var(--color-white)',
                checkmarkColor: 'var(--gray700)',
                hoverBorderColor: 'var(--gray800)',
                hoverBackgroundColor: 'var(--color-white)',
            },
            false: {
                // not selected
                borderColor: 'var(--gray200)',
                backgroundColor: 'var(--color-white)',
                checkmarkColor: 'var(--gray700)',
                hoverBorderColor: 'var(--gray200)',
                hoverBackgroundColor: 'var(--color-white)',
            },
        },
        success: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--color-success-default)',
                backgroundColor: 'var(--green50)',
                checkmarkColor: 'var(--color-success-default)',
                hoverBorderColor: 'var(--color-success-default)',
                hoverBackgroundColor: 'var(--green50)',
            },
            false: {
                // not selected
                borderColor: 'var(--gray800)',
                backgroundColor: 'var(--gray50)',
                checkmarkColor: 'var(--gray800)',
                hoverBorderColor: 'var(--gray800)',
                hoverBackgroundColor: 'var(--gray50)',
            },
        },
        error: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--color-error-default)',
                backgroundColor: 'var(--red50)',
                checkmarkColor: 'var(--color-error-default)',
                hoverBorderColor: 'var(--color-error-default)',
                hoverBackgroundColor: 'var(--red50)',
            },
            false: {
                // not selected
                borderColor: 'var(--color-error-default)',
                backgroundColor: 'var(--red50)',
                checkmarkColor: 'var(--color-error-default)',
                hoverBorderColor: 'var(--color-error-default)',
                hoverBackgroundColor: 'var(--red50)',
            },
        },
    },
    false: {
        // not disabled
        normal: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--blue500)',
                backgroundColor: 'var(--blue50)',
                checkmarkColor: 'var(--blue500)',
                hoverBorderColor: 'var(--blue500)',
                hoverBackgroundColor: 'var(--blue50)',
            },
            false: {
                // not selected
                borderColor: 'var(--gray200)',
                backgroundColor: 'var(--color-white)',
                checkmarkColor: 'var(--blue500)',
                hoverBorderColor: 'var(--gray700)',
                hoverBackgroundColor: 'var(--gray50)',
            },
        },
        success: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--color-success-default)',
                backgroundColor: 'var(--green50)',
                checkmarkColor: 'var(--color-success-default)',
                hoverBorderColor: 'var(--color-success-default)',
                hoverBackgroundColor: 'var(--green50)',
            },
            false: {
                // not selected
                borderColor: 'var(--color-success-default)',
                backgroundColor: 'var(--green50)',
                checkmarkColor: 'var(--color-success-default)',
                hoverBorderColor: 'var(--color-success-default)',
                hoverBackgroundColor: 'var(--green50)',
            },
        },
        error: {
            // highlight
            true: {
                // selected
                borderColor: 'var(--color-error-default)',
                backgroundColor: 'var(--red50)',
                checkmarkColor: 'var(--color-error-default)',
                hoverBorderColor: 'var(--color-error-default)',
                hoverBackgroundColor: 'var(--red50)',
            },
            false: {
                // not selected
                borderColor: 'var(--color-error-default)',
                backgroundColor: 'var(--red50)',
                checkmarkColor: 'var(--color-error-default)',
                hoverBorderColor: 'var(--color-error-default)',
                hoverBackgroundColor: 'var(--red50)',
            },
        },
    },
};

const selectIcon = (highlight, checked) =>
    highlight === 'error'
        ? CheckBoxErrorIcon
        : highlight === 'success'
          ? CheckBoxCorrectIcon
          : checked
            ? CheckBoxSelectedIcon
            : CheckBoxUnselectedIcon;

const CheckBox = ({ name, disabled = false, selected = false, checked, highlight = 'normal', onChange, children, value }) => {
    const CheckIcon = selectIcon(highlight, checked);
    return (
        <Container disabled={disabled} selected={selected} highlight={highlight}>
            <Check
                disabled={disabled}
                type="checkbox"
                checked={checked}
                name={name}
                onChange={onChange}
                value={value}
                aria-invalid={highlight === HIGHLIGHT_TYPES.ERROR}
            />
            <CheckIcon />
            <Content>
                <MathContent>{children}</MathContent>
            </Content>
        </Container>
    );
};

CheckBox.propTypes = {
    name: PropTypes.string,
    disabled: PropTypes.bool,
    selected: PropTypes.bool,
    checked: PropTypes.bool,
    highlight: PropTypes.oneOf(HIGHLIGHT_TYPES),
    onChange: PropTypes.func,
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired,
    value: PropTypes.number.isRequired,
};

export default CheckBox;
